'use client';

import { usePathname } from 'next/navigation';
import { ReactNode, useState, useEffect, useLayoutEffect } from 'react';
import { useSidebar } from '@/contexts/SidebarContext';
import { watchBannerHeights } from './bannerHeightWatcher';
import { SUB_BANNER_VAR } from '@/lib/layout/subBannerPrePaint';

/**
 * ConditionalMain - Wraps the main content. Applies margin-top for navbar and
 * margin-left for persistent left sidebar (desktop only, YouTube-style).
 */
export default function ConditionalMain({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const { width: sidebarWidth, restored: sidebarRestored } = useSidebar();
  const [isDesktop, setIsDesktop] = useState(false);
  // null = not measured yet. The server paint already reserves the bar through CSS (html:has([data-alert-banner])
  // in app/globals.css), so nothing may overwrite that with 0 before the first real measurement lands
  // (register promo-bar-offset-set-after-hydration-1031).
  const [bannerHeight, setBannerHeight] = useState<number | null>(null);
  // The fixed sub-banner slot (premium/verify bars) is cleared through the --sub-banner-h VAR, not state: the inline
  // script after the slot (src/lib/layout/subBannerPrePaint.ts) sets it while the HTML parses, so <main> is already
  // below the bar at server paint. State started at 0 and pushed the page down 112px after hydration on a phone
  // (register signed-in-visitors-still-shift-sitewide-1103). These writes only keep it current.
  const setSubBannerHeight = (h: number) => document.documentElement.style.setProperty(SUB_BANNER_VAR, `${h}px`);

  useEffect(() => {
    const update = () => setIsDesktop(typeof window !== 'undefined' && window.innerWidth >= 768);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Synchronous measurement before first paint (useLayoutEffect) eliminates the initial
  // layout shift where banner covers top of page content.
  // Then ResizeObserver keeps height in sync when banner is dismissed or resizes.
  useLayoutEffect(() => {
    // Read banner height synchronously — fires before browser paints so there's no CLS.
    const syncBannerHeight = () => {
      const el = document.querySelector('[data-alert-banner]');
      setBannerHeight(el ? el.getBoundingClientRect().height : 0);
      const sub = document.querySelector('[data-sub-banner]');
      setSubBannerHeight(sub ? sub.getBoundingClientRect().height : 0);
    };
    syncBannerHeight();

    // Then follow mount/unmount and resizes WITHOUT a forced layout per DOM change (see the module).
    return watchBannerHeights(setBannerHeight, setSubBannerHeight);
  }, []);

  const noNavbarRoutes = ['/stream', '/labs/workspace'];
  const [embed, setEmbed] = useState(false);
  useEffect(() => {
    try { setEmbed(new URLSearchParams(window.location.search).get('embed') === '1'); } catch { /* ssr */ }
  }, [pathname]);
  const shouldHideNavbar = noNavbarRoutes.includes(pathname) || embed;

  // ⭐ PUBLISH THE SIDEBAR WIDTH GLOBALLY, exactly like AlertsChatDock already publishes
  // --alerts-chat-right-pad on documentElement.
  // 🚨 WHY: this value used to live ONLY as an inline style on <main>, so it inherited only into
  // main's subtree. Anything rendered outside it (or before ConditionalMain's isDesktop effect had
  // run) read the `0px` fallback instead. The access gate spent six deploys being off-centre on
  // exactly that: one side of its band came from a var it could see, the other from a var it could
  // not, so it centred against a band that was half real. On documentElement both vars inherit
  // everywhere and are live, which is what makes the pure-CSS centring in PremiumContentGate work
  // with no JS, no polling and nothing to go stale.
  // 🚨 NOTHING IS WRITTEN UNTIL THE SIDEBAR HAS RESTORED ITS STORED STATE, and the width test reads the window,
  // not the isDesktop state (which starts false). The <head> pre-paint script (src/lib/layout/railsPrePaint.ts)
  // has already set the real value; the old first pass wrote 0px (isDesktop false), then 368, then 72 for a
  // collapsed visitor, and <main> reflowed at every step (register desktop-rails-mount-after-hydration-cls-1102).
  useEffect(() => {
    if (!sidebarRestored) return;
    const desktop = window.innerWidth >= 768;
    const px = !shouldHideNavbar && desktop ? `${sidebarWidth}px` : '0px';
    document.documentElement.style.setProperty('--sidebar-width', px);
  }, [sidebarWidth, isDesktop, shouldHideNavbar, sidebarRestored]);


  // PUBLISH THE ALERT-BANNER HEIGHT THE SAME WAY, AND FOR THE SAME REASON as --sidebar-width above.
  // 🚨 WHY (register: mobile-alertbanner-covers-hamburger). AlertBanner is `fixed top-0 z-[9999]` and
  // the navbar header is `fixed top-0 z-30`, so the banner sat ON TOP OF THE WHOLE HEADER. Measured
  // live 2026-09-14 with document.elementFromPoint - the browser's own hit test, so this is a fact
  // about clicks, not a rendering artifact - ZERO header controls were reachable while the banner
  // showed: at 375px the hamburger, logo, search and Start Free; at 1440px all eleven, Sign In and
  // Start Free included.
  // ⚠ THE ENTRY SAID "DESKTOP IS UNAFFECTED". That was wrong. Desktop only LOOKS fine because the
  // left sidebar is a separate element further down the page, so navigation still works; every
  // control in the header row itself was just as dead as on the phone.
  // ⭐ So the header and everything anchored beneath it now STACK below the banner instead of sharing
  // top-0 with it. This var is the single source of truth for that offset, set on documentElement so
  // it inherits everywhere (the same lesson the --sidebar-width comment above records).
  // ⚠ useLayoutEffect, not useEffect: the header's top now comes from this var, so publishing it
  // after paint would show one frame of the header sitting under the banner and then snapping down.
  // That is the exact CLS the measurement above already uses useLayoutEffect to avoid.
  useLayoutEffect(() => {
    if (bannerHeight === null) return;
    document.documentElement.style.setProperty('--alert-banner-h', `${bannerHeight}px`);
  }, [bannerHeight]);

  const NAVBAR_HEIGHT = 64;
  const style: React.CSSProperties = {};
  // ⭐ SUM, NOT Math.max. The old max() encoded the OVERLAP: content cleared whichever of the banner
  // and the header was taller, because the two were drawn on top of each other. Now they stack, so
  // the content has to clear both. With the banner dismissed bannerHeight is 0 and this is exactly
  // the old value, which is why a returning visitor sees no change at all.
  // The banner term is the CSS VAR, not the state: the var is right at server paint (globals.css sets it when
  // the bar is in the HTML) and the state is not, so a state-based margin moved all content down 47px at
  // hydration on every page (CLS ~0.056, register promo-bar-offset-set-after-hydration-1031).
  if (!shouldHideNavbar) style.marginTop = `calc(var(--alert-banner-h, 0px) + var(${SUB_BANNER_VAR}, 0px) + ${NAVBAR_HEIGHT}px)`;
  // ⭐ THE HORIZONTAL INSETS ARE CSS NOW, NOT INLINE STATE: `main#main[data-rails]` in app/globals.css applies
  // margin-left: var(--sidebar-width) and padding-right: var(--alerts-chat-right-pad) at >=768px. Those vars
  // are right at the SERVER PAINT (pre-paint script), so <main> never moves at hydration. The old inline
  // marginLeft only existed once isDesktop flipped true AFTER hydration, which put main at x0 w1440 for the
  // first paint and slid it 368px (measured desktop CLS 0.31-1.45, register
  // desktop-rails-mount-after-hydration-cls-1102).
  // No-navbar routes keep a local 0px so nothing inside them inherits the sidebar width.
  if (shouldHideNavbar) (style as Record<string, string>)['--sidebar-width'] = '0px';
  // 🚨 ONE INSET OWNER: EVERY NAVBAR ROUTE GETS data-rails, /alerts INCLUDED (register
  // alerts-rails-exemption-slides-main-on-every-crossing-1104). /alerts used to be exempt and inset its own content,
  // so <main> was x0 there and x368 everywhere else, and every client navigation into or out of /alerts slid
  // <main> 368px through its 200ms margin transition. Measured on prod at 1440: 0.219 counted CLS going in, 0.249
  // going out, all of it on main#main, the exact target the real-visitor rows name. The same split double-inset
  // anything that added the margin itself inside a railed page: the paywall card sat 184px right of centre on
  // /chart, and the /alerts SEO section rendered at x777 w622 on /alerts/rvol instead of x409 w990.
  // ⭐ The old reason for the exemption is gone: in 2026-08 the margin came from `isDesktop` state, which read
  // false on /alerts at 1920px. It now comes from CSS (main#main[data-rails] at >=768px, from the pre-painted var),
  // so no client state is involved and /alerts gets exactly the inset every other page gets.
  // ⛔ So nothing INSIDE <main> may add margin-left: var(--sidebar-width) or padding-right from the dock var again.
  // Fixed-position chrome (SubBannerSlot, BandCentered, the /chart header) still reads the vars for left/right:
  // that is correct, a fixed box does not inherit main's margin. layoutInsetOwner.test.ts enforces both halves.
  const rails = !shouldHideNavbar;

  const minHeightClass = shouldHideNavbar ? 'min-h-dvh' : 'min-h-[calc(100dvh-4rem)]';
  // 🚨 `overflow-auto` IS NOW SCOPED TO THE PAGES THAT ASKED FOR IT, AND THAT IS THE WHOLE FIX.
  // It arrived on this SHARED element in 49da85bd0, "Hide navbar on stream page for full-screen
  // experience" - a one-page change. The result: every route scrolled inside <main> instead of the
  // document, so `window.scrollY` was permanently 0 sitewide. Measured on prod 2026-10-21:
  //     window.scrollBy(0,700) moved NOTHING (winY 0), while main held 12,766px in a 704px box.
  // ⛔ THE CONSEQUENCES WERE ALREADY IN THE CODE, not theoretical: Navbar.tsx gates a header shadow
  // on `window.scrollY > 10` and listens on WINDOW, so it never fires (scroll does not bubble from
  // an element); StickyStockNav.tsx:73 carries `scroller ? scroller.scrollTop : window.scrollY`,
  // which is somebody's local workaround for exactly this; and StockScrollDock.tsx works only
  // because window.scrollY happens to always be 0.
  // ⚠ SCOPING THIS ALONE IS NOT ENOUGH - globals.css also pinned `html, body { height: 100% }` and
  // made body an `overflow-y: auto` scroller. All three had to go together; fixing only one leaves
  // the document still unable to scroll (tried, and it failed exactly that way).
  // ⭐ shouldHideNavbar is the stream/labs-workspace/embed case - the full-screen shell that
  // legitimately wants its own scroll container. It already drives minHeightClass above.
  const scrollClass = shouldHideNavbar ? 'overflow-auto' : '';
  return (
    <main id="main" tabIndex={-1} className={`dt-gated-content flex-1 ${minHeightClass} ${scrollClass} transition-[margin-left,padding-right] duration-200 ease-out`} style={style} data-rails={rails ? '' : undefined}>
      {children}
    </main>
  );
}
